/**
 * Official Audit Checklist Page
 *
 * Interactive audit checklist for conducting official halal certification audits.
 * Uses existing checklist components with auditor-specific workflow.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Save, BarChart3, FileText, Check } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { ProgressBar } from '../../components/common/ProgressBar';

export const OfficialAuditChecklistPage: React.FC = () => {
  const { t } = useTranslation(['common', 'auditor']);
  const { id } = useParams<{ id: string }>();

  const auditInfo = {
    company: 'Kantin Warisan Sdn Bhd',
    applicationId: id || 'app-001',
    auditDate: new Date().toISOString(),
    auditor: 'Ustaz Mohamed bin Ibrahim',
    auditType: 'Official Certification Audit',
  };

  return (
    <PageLayout
      pageTitle={t('auditor:auditChecklist.title')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('auditor:dashboard.title'), href: '/auditor/dashboard' },
        { label: t('auditor:applications.applicationQueue'), href: '/auditor/applications' },
        { label: t('auditor:auditChecklist.title') },
      ]}
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => alert('Save progress')} icon={<Save className="w-4 h-4" />}>
            Save Progress
          </Button>
          <Button onClick={() => alert('Generate audit report')} icon={<BarChart3 className="w-4 h-4" />}>
            {t('auditor:auditChecklist.subtitle')}
          </Button>
        </div>
      }
    >
      {/* Audit Information */}
      <Card className="mb-6">
        <CardHeader>
          <h3 className="text-lg font-semibold">Audit Information</h3>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Company</p>
              <p className="mt-1">{auditInfo.company}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Application ID</p>
              <p className="mt-1 font-mono text-sm">{auditInfo.applicationId}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Audit Date</p>
              <p className="mt-1">{new Date(auditInfo.auditDate).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Auditor</p>
              <p className="mt-1">{auditInfo.auditor}</p>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-500">Audit Type</p>
            <Badge variant="info" className="mt-1">{auditInfo.auditType}</Badge>
          </div>
        </CardBody>
      </Card>

      {/* Overall Progress */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold">Overall Audit Progress</h3>
            <span className="text-sm text-gray-600">0%</span>
          </div>
          <ProgressBar value={0} variant="default" />
        </CardBody>
      </Card>

      {/* Audit Categories */}
      <div className="space-y-4 mb-6">
        <h3 className="text-xl font-bold text-gray-800">Audit Categories</h3>

        {/* Management Category */}
        <Card>
          <CardHeader>
            <h4 className="font-semibold">{t('auditor:auditChecklist.categories.management')}</h4>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input type="checkbox" className="w-5 h-5" />
                <span>Halal Policy documented and communicated</span>
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" className="w-5 h-5" />
                <span>Management review conducted regularly</span>
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" className="w-5 h-5" />
                <span>Halal committee established</span>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Premises Category */}
        <Card>
          <CardHeader>
            <h4 className="font-semibold">{t('auditor:auditChecklist.categories.premises')}</h4>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input type="checkbox" className="w-5 h-5" />
                <span>Facilities segregated from non-halal activities</span>
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" className="w-5 h-5" />
                <span>Cleaning and sanitation procedures adequate</span>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Hygiene Category */}
        <Card>
          <CardHeader>
            <h4 className="font-semibold">{t('auditor:auditChecklist.categories.hygiene')}</h4>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input type="checkbox" className="w-5 h-5" />
                <span>Personal hygiene practices observed</span>
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" className="w-5 h-5" />
                <span>Pest control program in place</span>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Production Category */}
        <Card>
          <CardHeader>
            <h4 className="font-semibold">{t('auditor:auditChecklist.categories.production')}</h4>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input type="checkbox" className="w-5 h-5" />
                <span>Production process documented</span>
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" className="w-5 h-5" />
                <span>Critical control points identified</span>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Audit Summary and Actions */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Audit Summary</h3>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600">Conformities</p>
              <p className="text-3xl font-bold text-green-600">0</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-sm text-gray-600">Minor NC</p>
              <p className="text-3xl font-bold text-yellow-600">0</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-gray-600">Major NC</p>
              <p className="text-3xl font-bold text-red-600">0</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Observations</p>
              <p className="text-3xl font-bold text-blue-600">0</p>
            </div>
          </div>

          <div className="flex gap-4">
            <Button variant="outline" fullWidth onClick={() => window.location.href = `/auditor/ncr/create?app=${id}`} icon={<FileText className="w-4 h-4" />}>
              Create NCR
            </Button>
            <Button fullWidth onClick={() => alert('Finalize audit report')} icon={<Check className="w-4 h-4" />}>
              Finalize Audit
            </Button>
          </div>
        </CardBody>
      </Card>
    </PageLayout>
  );
};

